import React, { useState, useEffect } from 'react';
import { FileWarning, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { documentsHealthAPI } from '../../services/api';

const STORAGE_LABEL = {
  s3: 'S3 (permanente)',
  locale: 'Disco locale (effimero)',
  esterno: 'URL esterno',
  assente: 'Nessun riferimento',
};

export const DocumentsHealthCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await documentsHealthAPI.check();
      setData(res.data);
    } catch (e) {
      toast.error('Errore nel controllo dei documenti');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <Card className="glass-card" data-testid="documents-health-card">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <FileWarning className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Stato documenti</CardTitle>
              <CardDescription>
                Verifica che ogni PDF collegato alle pagine compliance, alle pagine personalizzate e alle
                certificazioni sia ancora scaricabile. I documenti rotti vanno ricaricati a mano.
              </CardDescription>
            </div>
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="documents-health-recheck-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Ricontrolla
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!data ? (
          <p className="text-sm text-muted-foreground">Controllo in corso...</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-3 text-sm" data-testid="documents-health-summary">
              <span>Documenti controllati: <strong>{data.total}</strong></span>
              <span className="text-emerald-600 dark:text-emerald-400">OK: <strong>{data.ok}</strong></span>
              <span className={data.broken ? 'text-destructive' : ''}>Rotti: <strong>{data.broken}</strong></span>
            </div>

            {data.broken > 0 && (
              <div
                className="flex items-start gap-3 p-4 rounded-lg border border-destructive/40 bg-destructive/10 text-sm"
                data-testid="documents-health-alert"
              >
                <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                <p>
                  <strong>{data.broken} documento/i da ricaricare a mano.</strong> I file erano stati caricati
                  prima dell'attivazione dello storage permanente e sono andati persi a un riavvio. Apri la voce
                  corrispondente e ricarica il PDF: i nuovi caricamenti finiscono su S3 e non si perdono più.
                </p>
              </div>
            )}

            {data.local_refs > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="documents-health-local-note">
                {data.local_refs} riferimento/i punta ancora al disco locale (storage effimero): anche se ora
                risulta OK, il file può sparire al prossimo riavvio. Conviene ricaricarlo per spostarlo su S3.
              </p>
            )}

            <div className="space-y-2">
              {data.items.length === 0 && (
                <p className="text-sm text-muted-foreground">Nessun PDF collegato al momento.</p>
              )}
              {data.items.map((item) => (
                <div
                  key={`${item.collection}-${item.key}`}
                  className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-muted/40"
                  data-testid={`document-health-item-${item.collection}-${item.key}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">{item.source}</p>
                    <p className="text-xs text-muted-foreground break-all">{item.url}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {STORAGE_LABEL[item.storage] || item.storage}
                      {item.http_status ? ` · HTTP ${item.http_status}` : ''}
                      {item.detail ? ` · ${item.detail}` : ''}
                    </p>
                  </div>
                  {item.status === 'OK' ? (
                    <Badge variant="outline" className="border-emerald-500/50 text-emerald-600 dark:text-emerald-400">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      OK
                    </Badge>
                  ) : (
                    <Badge variant="destructive" data-testid={`document-health-broken-${item.collection}-${item.key}`}>
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Rotto · da ricaricare
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentsHealthCard;
